/**
 * The subscription plans: what each costs and what it allows. The server
 * enforces the limits; the app and the pricing page describe them. Prices
 * are in US dollars and are the only currency sold.
 *
 * Changing a price here is half the job: run `npm run stripe:setup` so the
 * matching prices exist in Stripe.
 */

export type PlanId = 'free' | 'pro' | 'business';
export type PaidPlanId = Exclude<PlanId, 'free'>;
export type BillingInterval = 'month' | 'year';

export const CURRENCY = 'usd';
const GB = 1024 ** 3;

export interface PlanLimits {
  /** People in one meeting, the host included. null: no limit. */
  maxParticipants: number | null;
  /** How long one meeting may run, in minutes. null: no limit. */
  maxMeetingMinutes: number | null;
  recording: boolean;
  /** Space for recordings, in bytes. */
  storageBytes: number;
  /** 720p video. Without it the camera is sent at 360p. */
  hdVideo: boolean;
  /** Sharing a recording, with particular people by email or with a link. */
  shareRecordings: boolean;
}

export interface Plan {
  id: PlanId;
  name: string;
  /** One line under the name on the pricing page. */
  summary: string;
  /** Price in US cents for one month, and for one year paid at once. */
  price: Record<BillingInterval, number>;
  limits: PlanLimits;
}

export const PLANS: Record<PlanId, Plan> = {
  free: {
    id: 'free',
    name: 'Free',
    summary: 'For catching up with a few people.',
    price: { month: 0, year: 0 },
    limits: { maxParticipants: 4, maxMeetingMinutes: 40, recording: false, storageBytes: 0, hdVideo: false, shareRecordings: false },
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    summary: 'For people who run meetings as part of their work.',
    price: { month: 900, year: 9000 },
    limits: { maxParticipants: 15, maxMeetingMinutes: null, recording: true, storageBytes: 10 * GB, hdVideo: true, shareRecordings: true },
  },
  business: {
    id: 'business',
    name: 'Business',
    summary: 'For teams with larger meetings and more to keep.',
    price: { month: 1900, year: 19000 },
    limits: { maxParticipants: 25, maxMeetingMinutes: null, recording: true, storageBytes: 50 * GB, hdVideo: true, shareRecordings: true },
  },
};

export const PLAN_ORDER: readonly PlanId[] = ['free', 'pro', 'business'];
export const PAID_PLANS: readonly PaidPlanId[] = ['pro', 'business'];
export const BILLING_INTERVALS: readonly BillingInterval[] = ['month', 'year'];

/**
 * The largest video size the media server forwards: the camera is sent in
 * sizes 0 (180p), 1 (360p) and, with HD, 2 (720p). A plan without HD never
 * receives size 2, whatever the app asks for.
 */
export const allowedVideoLayer = (requested: number, hdVideo: boolean): number => (hdVideo ? requested : Math.min(requested, 1));

/** How long before a meeting's time limit everyone is warned. */
export const TIME_LIMIT_WARNING_MINUTES = 5;

export const isPaidPlan = (value: unknown): value is PaidPlanId => value === 'pro' || value === 'business';
export const isBillingInterval = (value: unknown): value is BillingInterval => value === 'month' || value === 'year';

/** The higher of two plans. */
export function higherPlan(a: PlanId, b: PlanId): PlanId {
  return PLAN_ORDER.indexOf(a) >= PLAN_ORDER.indexOf(b) ? a : b;
}

/**
 * The name a price is known by in Stripe. Prices are found by this key, not
 * by ID, so the same code works in Stripe's test and live modes.
 */
export const priceLookupKey = (plan: PaidPlanId, interval: BillingInterval): string => `meetdost_${plan}_${interval === 'month' ? 'monthly' : 'yearly'}`;

export function planFromLookupKey(key: string | null | undefined): { plan: PaidPlanId; interval: BillingInterval } | null {
  const match = /^meetdost_(pro|business)_(monthly|yearly)$/.exec(key ?? '');
  return match ? { plan: match[1] as PaidPlanId, interval: match[2] === 'monthly' ? 'month' : 'year' } : null;
}

/**
 * Whether a Stripe subscription status means the plan is in force. A
 * payment that is late (`past_due`) keeps the plan while Stripe retries the
 * card; once Stripe gives up the status becomes `canceled` or `unpaid`.
 */
export const isLiveSubscription = (status: string): boolean => status === 'active' || status === 'trialing' || status === 'past_due';

/**
 * What the plan page calls the button that opens the billing page. It names
 * what the person most likely came to do, given the state of what they pay
 * for. The payment sandbox's billing page has no payment details to update.
 *
 * It is here, not in the app, because emails name the same button, and an
 * email that calls it something else sends people looking for a button that
 * is not there.
 */
export function manageLabel(paid: { status: string; cancelAtPeriodEnd: boolean }, sandbox = false): 'Update payment details' | 'Keep my plan' | 'Manage billing' {
  if (paid.status === 'past_due') return sandbox ? 'Manage billing' : 'Update payment details';
  return paid.cancelAtPeriodEnd ? 'Keep my plan' : 'Manage billing';
}

/** "$9" or "$7.50": whole dollars without cents. */
export function formatUsd(cents: number): string {
  const dollars = cents / 100;
  return `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}`;
}

/** What a year costs per month, in cents, for showing "$7.50 a month, billed yearly". */
export const monthlyEquivalent = (plan: Plan): number => Math.round(plan.price.year / 12);

/** How much paying yearly saves compared with twelve monthly payments, as a whole percentage. */
export function yearlySavingPct(plan: Plan): number {
  const twelveMonths = plan.price.month * 12;
  return twelveMonths === 0 ? 0 : Math.round(((twelveMonths - plan.price.year) / twelveMonths) * 100);
}

/** "10 GB" */
export const formatStorage = (bytes: number): string => `${Math.round(bytes / GB)} GB`;

/** One line of a pricing card. `included` is false for something the plan lacks. */
export interface PlanFeature {
  text: string;
  included: boolean;
}

/** What a set of limits allows, as the lines of a pricing card. */
export function limitFeatures(l: PlanLimits): PlanFeature[] {
  return [
    { text: l.maxParticipants === null ? 'Any number of people in a meeting' : `Up to ${l.maxParticipants} people in a meeting`, included: true },
    { text: l.maxMeetingMinutes === null ? 'Meetings as long as you need' : `Meetings up to ${l.maxMeetingMinutes} minutes`, included: true },
    { text: l.recording ? `Recording, with ${formatStorage(l.storageBytes)} of storage` : 'No recording', included: l.recording },
    { text: l.hdVideo ? 'HD video (720p)' : 'Standard video (360p)', included: true },
    ...(l.shareRecordings ? [{ text: 'Share recordings by email or with a link', included: true }] : []),
  ];
}

/** The plan's limits in words, one line each. */
export const planHighlights = (plan: Plan): string[] => limitFeatures(plan.limits).map((f) => f.text);

/** What every plan includes, shown once under the pricing cards. */
export const INCLUDED_IN_EVERY_PLAN: readonly string[] = [
  'A waiting room on every meeting: nobody gets in until you admit them',
  'Guests join from a link, with no account and nothing to install',
  'Host controls: mute, stop video, remove, lock, co-hosts',
  'Screen sharing, chat, raised hands and reactions',
  'Chat kept after the call',
  'Works in the browser, on a computer or a phone',
];
